import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import toast from "react-hot-toast";
import { useNavigate } from "@tanstack/react-router";
import { supabase } from "../../lib/supabase";
import { queryKeys } from "../../lib/queryKeys";
import { useAuth } from "../../contexts/AuthContext";
import { uploadTaskAttachment } from "../../lib/storage";
import { showTaskLinkToast } from "../../lib/undoStack";
import { NewTask, Task, SubTask, Tag, TaskAttachment, TaskLink } from "../../types";
import { DEFAULT_LIST_EMOJI } from "../../constants/defaults";
import { placeCreatedTask } from "../../lib/placeTaskInCache";

export function useCreateTask() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async ({
      newTask,
      options,
    }: {
      newTask: NewTask;
      options?: { silent?: boolean };
    }): Promise<Task> => {
      if (!user) throw new Error("Not authenticated");

      // Read the freshest task list at mutation time (not stale render-time
      // snapshot) to compute the next order_position.
      const currentTasks = queryClient.getQueryData<Task[]>(queryKeys.tasks) ?? [];
      const sameListTasks = currentTasks.filter(
        (t) => t.list_id === newTask.list_id
      );
      const maxOrder =
        sameListTasks.length > 0
          ? Math.max(...sameListTasks.map((t) => t.order_position))
          : 0;

      const { data: taskData, error: taskError } = await supabase
        .from("tasks")
        .insert({
          user_id: user.id,
          list_id: newTask.list_id,
          title: newTask.title,
          description: newTask.description,
          priority: newTask.priority,
          scheduled_date: newTask.scheduled_date || null,
          scheduled_time: newTask.scheduled_time || null,
          deadline_date: newTask.deadline_date || null,
          duration: newTask.duration ?? null,
          persistent: newTask.persistent ?? false,
          working_hours_only: newTask.working_hours_only ?? false,
          recurrence_frequency: newTask.recurrence_frequency ?? null,
          recurrence_interval: newTask.recurrence_interval ?? 1,
          recurrence_day_of_week: newTask.recurrence_day_of_week ?? null,
          recurrence_end_date: newTask.recurrence_end_date ?? null,
          order_position: maxOrder + 100,
        })
        .select()
        .single();

      if (taskError) throw taskError;

      // Subtasks
      let subtasks: SubTask[] = [];
      if (newTask.subtasks && newTask.subtasks.length > 0) {
        const { data: subtasksData, error: subtasksError } = await supabase
          .from("subtasks")
          .insert(
            newTask.subtasks.map((st, idx) => ({
              task_id: taskData.id,
              title: st.title,
              description: st.description ?? "",
              priority: st.priority ?? 4,
              scheduled_date: st.scheduled_date ?? null,
              scheduled_time: st.scheduled_time ?? null,
              deadline_date: st.deadline_date ?? null,
              duration: st.duration ?? null,
              completed: false,
              order_position: (idx + 1) * 100,
            }))
          )
          .select();

        if (subtasksError) {
          showTaskLinkToast({
            label: `"${taskData.title}"`,
            labelSuffix: " created, but subtasks failed to save.",
            type: "error",
            onLabelClick: () =>
              navigate({
                to: "/task/$taskId",
                params: { taskId: taskData.id },
                search: (prev: Record<string, unknown>) => prev,
              }),
          });
        } else {
          subtasks = (subtasksData ?? []).map((st) => ({
            ...st,
            tags: [],
            attachments: [],
            links: [],
          }));
        }
      }

      // Tags
      let taskTags: Tag[] = [];
      if (newTask.tag_ids && newTask.tag_ids.length > 0) {
        const { error: tagError } = await supabase
          .from("task_tags")
          .insert(
            newTask.tag_ids.map((tagId) => ({
              task_id: taskData.id,
              tag_id: tagId,
            }))
          );

        if (tagError) {
          Sentry.captureException(tagError, {
            extra: { mutation: "useCreateTask/tags", taskId: taskData.id },
          });
        } else {
          // Resolve tag objects from the cache for immediate UI feedback.
          // The subsequent invalidation will bring the authoritative data.
          const allTags =
            queryClient.getQueryData<Tag[]>(queryKeys.tags) ?? [];
          taskTags = allTags.filter((t) => newTask.tag_ids?.includes(t.id));
        }
      }

      // Attachments
      const taskAttachments: TaskAttachment[] = [];
      if (newTask.pending_files && newTask.pending_files.length > 0) {
        for (const file of newTask.pending_files) {
          try {
            const uploadResult = await uploadTaskAttachment(
              file,
              user.id,
              taskData.id
            );
            const { data: attachmentData, error: attachmentError } =
              await supabase
                .from("task_attachments")
                .insert({
                  task_id: taskData.id,
                  file_name: uploadResult.file_name,
                  file_path: uploadResult.file_path,
                  file_size: uploadResult.file_size,
                  mime_type: uploadResult.mime_type,
                  storage_url: uploadResult.storage_url,
                })
                .select()
                .single();

            if (attachmentError) {
              Sentry.captureException(attachmentError, {
                extra: { mutation: "useCreateTask/attachment", taskId: taskData.id },
              });
              toast.error(`Failed to save attachment "${file.name}".`);
            } else if (attachmentData) {
              taskAttachments.push(attachmentData as TaskAttachment);
            }
          } catch (err) {
            Sentry.captureException(err, {
              extra: { mutation: "useCreateTask/upload", taskId: taskData.id },
            });
            toast.error(`Failed to upload "${file.name}".`);
          }
        }
      }

      // Links
      let taskLinks: TaskLink[] = [];
      if (newTask.links && newTask.links.length > 0) {
        const { data: linksData, error: linksError } = await supabase
          .from("task_links")
          .insert(
            newTask.links.map((link) => ({
              task_id: taskData.id,
              url: link.url,
              description: link.description,
            }))
          )
          .select();

        if (linksError) {
          Sentry.captureException(linksError, {
            extra: { mutation: "useCreateTask/links", taskId: taskData.id },
          });
        } else {
          taskLinks = (linksData ?? []) as TaskLink[];
        }
      }

      // Resolve list display name from cache for calendar sync.
      const allTasks = queryClient.getQueryData<Task[]>(queryKeys.tasks) ?? [];
      const listLookup = allTasks.find((t) => t.list_id === newTask.list_id);

      const fullTask: Task = {
        ...(taskData as unknown as Task),
        subtasks,
        tags: taskTags,
        attachments: taskAttachments,
        links: taskLinks,
        list: listLookup?.list ?? "",
        listEmoji: listLookup?.listEmoji ?? DEFAULT_LIST_EMOJI,
      };

      if (!options?.silent) {
        showTaskLinkToast({
          label: `"${taskData.title}"`,
          labelSuffix: " created",
          onLabelClick: () =>
            navigate({
              to: "/task/$taskId",
              params: { taskId: taskData.id },
              search: (prev: Record<string, unknown>) => prev,
            }),
        });
      }

      Sentry.addBreadcrumb({
        category: "task",
        message: "Task created",
        data: { taskId: fullTask.id },
        level: "info",
      });

      return fullTask;
    },

    onSuccess: (fullTask) => {
      // Land the created task in the cache before the refetch. mutationFn
      // derives the next order_position from this same cache, so callers that
      // create several tasks in an awaited loop — the voice-review accept and
      // Captures approve flows — would otherwise give every task the same
      // position and end up with an unstable order. Skipped when the cache has
      // not loaded, so that an early create can't flip the query out of its
      // pending state with a one-task list. If the live insert refetch already
      // wrote this row, replace that copy instead of appending a second card.
      queryClient.setQueryData<Task[]>(queryKeys.tasks, (prev) =>
        placeCreatedTask(prev, fullTask),
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks });
    },

    onError: (err, { newTask }) => {
      Sentry.captureException(err, {
        extra: { mutation: "useCreateTask", title: newTask.title },
      });
      toast.error(`Failed to create "${newTask.title}". Please try again.`);
    },
  });
}
